type AudioName =
  | 'auction_reveal'
  | 'bid_lock'
  | 'countdown_tick'
  | 'bid_reveal'
  | 'win_auction'
  | 'lose_auction'
  | 'tie_auction'
  | 'going_once'
  | 'going_twice'
  | 'sold'
  | 'card_flip'
  | 'round_win'
  | 'round_lose'
  | 'match_victory'
  | 'match_defeat'
  | 'heartbeat_tick'
  | 'crowd_swell'
  | 'background_music_auction'
  | 'background_music_battle';

const AUDIO_PATHS: Record<AudioName, string> = {
  auction_reveal: '/audio/auction_reveal.mp3',
  bid_lock: '/audio/bid_lock.mp3',
  countdown_tick: '/audio/countdown_tick.mp3',
  bid_reveal: '/audio/bid_reveal.mp3',
  win_auction: '/audio/win_auction.mp3',
  lose_auction: '/audio/lose_auction.mp3',
  tie_auction: '/audio/tie_auction.mp3',
  going_once: '/audio/going_once.mp3',
  going_twice: '/audio/going_twice.mp3',
  sold: '/audio/sold.mp3',
  card_flip: '/audio/card_flip.mp3',
  round_win: '/audio/round_win.mp3',
  round_lose: '/audio/round_lose.mp3',
  match_victory: '/audio/match_victory.mp3',
  match_defeat: '/audio/match_defeat.mp3',
  heartbeat_tick: '/audio/heartbeat_tick.mp3',
  crowd_swell: '/audio/crowd_swell.mp3',
  background_music_auction: '/audio/background_music_auction.mp3',
  background_music_battle: '/audio/background_music_battle.mp3',
};

const audioCache = new Map<string, HTMLAudioElement>();
let currentMusic: HTMLAudioElement | null = null;
let currentMusicBaseVolume = 0.4;
let currentMusicRequest: { name: AudioName; volume: number } | null = null;

const AUDIO_MUTED_KEY = 'anime-draft-audio-muted';
const LEGACY_MUSIC_MUTED_KEY = 'anime-draft-music-muted';

export function isMusicMuted() {
  if (typeof window === 'undefined') return false;
  return (
    window.localStorage.getItem(AUDIO_MUTED_KEY) === 'true' ||
    window.localStorage.getItem(LEGACY_MUSIC_MUTED_KEY) === 'true'
  );
}

export function setMusicMuted(muted: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AUDIO_MUTED_KEY, String(muted));
  window.localStorage.removeItem(LEGACY_MUSIC_MUTED_KEY);

  if (muted) {
    if (currentMusic) {
      currentMusic.pause();
      currentMusic.currentTime = 0;
      currentMusic = null;
    }
    return;
  }

  if (currentMusicRequest) {
    playMusic(currentMusicRequest.name, currentMusicRequest.volume);
  }
}

function getAudio(src: string, loop = false) {
  if (typeof window === 'undefined') return null;
  let audio = audioCache.get(src);
  if (!audio) {
    audio = new Audio(src);
    audio.preload = 'auto';
    audioCache.set(src, audio);
  }
  audio.loop = loop;
  return audio;
}

export function playSfx(name: AudioName, volume = 0.9) {
  const src = AUDIO_PATHS[name];
  if (typeof window === 'undefined') return;
  if (isMusicMuted()) return;
  // Always create a fresh Audio for SFX so overlapping plays work reliably
  try {
    const audio = new Audio(src);
    audio.volume = volume;
    audio.preload = 'auto';
    const playPromise = audio.play();
    if (playPromise) {
      playPromise.catch(() => { /* ignore autoplay errors */ });
    }
  } catch {
    // ignore autoplay errors
  }
}

export function playMusic(name: AudioName, volume = 0.4) {
  currentMusicRequest = { name, volume };
  if (isMusicMuted()) {
    if (currentMusic) {
      currentMusic.pause();
      currentMusic.currentTime = 0;
      currentMusic = null;
    }
    return;
  }

  const src = AUDIO_PATHS[name];
  const audio = getAudio(src, true);
  if (!audio) return;
  if (currentMusic && currentMusic !== audio) {
    currentMusic.pause();
    currentMusic.currentTime = 0;
  }
  currentMusic = audio;
  currentMusicBaseVolume = volume;
  try {
    audio.volume = volume;
    void audio.play();
  } catch {
    // ignore autoplay errors
  }
}

export function stopMusic() {
  currentMusicRequest = null;
  if (!currentMusic) return;
  currentMusic.pause();
  currentMusic.currentTime = 0;
  currentMusic = null;
}

export function setMusicVolume(volume: number) {
  if (!currentMusic) return;
  currentMusic.volume = Math.max(0, Math.min(1, volume));
}

export function restoreMusicVolume() {
  if (!currentMusic) return;
  currentMusic.volume = Math.max(0, Math.min(1, currentMusicBaseVolume));
}

export function duckMusic(volume: number, durationMs = 600) {
  if (!currentMusic) return;
  const prev = currentMusic.volume;
  currentMusic.volume = Math.max(0, Math.min(1, volume));
  setTimeout(() => {
    if (currentMusic) currentMusic.volume = prev;
  }, durationMs);
}
